import "./Checkout.css";
import { useEffect, useState, useContext, useRef, useCallback } from "react";
import {
  Form,
  Input,
  Button,
  Radio,
  Alert,
  Space,
  Card,
  Dropdown,
  Row,
  Col,
  Select,
  Spin,
  Modal,
} from "antd";
import { useTranslation } from "react-i18next";
import ReactToPrint from "react-to-print";
import CurrencyFormatter from "../../../components/CurrencyFormatter";
import {
  AddonName,
  TransactionStatus,
  TransactionType,
} from "../../../configs/constants";
import { AppContext } from "../../../context/AppContextProvider";
import {
  createTickets,
  createTransactions,
  editTransaction,
  getBranchDiscounts,
  getCustomers,
  getRegions,
  getCities,
} from "../../../network/network";
import TransactionSlip from "../../../components/TransactionSlip/TransactionSlip";
import { useNavigate } from "react-router-dom";
import { CloseOutlined, DownOutlined } from "@ant-design/icons";
import AddCustomer from "../Customers/AddCustomer";
import { getEntityBranchId, isSameBranch } from "../../../utils/branchScope";
import { handleApiError, showAppError } from "../../../utils/errorHandler";

const Checkout = ({ injectedState, onDone, onBack }) => {
  const { authUser, roundOff, appConfig } = useContext(AppContext);

  const [cartData, SetCartData] = useState([]);
  const [discountValue, setDiscountValue] = useState(0);
  const [discountId, setDiscountId] = useState("");
  const [totalAmount, setTotalAmount] = useState(0);
  const [grandTotalAmount, setGrandTotalAmount] = useState(0);
  const [totalTax, setTotalTax] = useState(0);
  const [grandtotalTax, setGrandTotalTax] = useState(0);

  const [selectedPaymentType, setSelectedPaymentType] = useState(
    TransactionType.Cash,
  );
  const [transaction, setTransaction] = useState(false);
  const [returnAmountMsg, setReturnAmountMsg] = useState("");
  const [cashCollected, setCashCollected] = useState("");
  const [branchDiscounts, setBranchDiscounts] = useState([]);
  const [onScreenInput, setOnScreenInput] = useState("");
  const [selectedField, setSelectedField] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [customerData, setCustomerData] = useState([]);
  const [reloadCustomerData, setReloadCustomerData] = useState(false);
  const [customerType, setCustomerType] = useState("individual");

  const [regions, setRegions] = useState([]);
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);

  const [checkoutForm] = Form.useForm();
  const transactionSlipRef = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTicketIssuing, setIsTicketIssuing] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const state = injectedState;

  const is_customer_management_active = authUser?.active_addons?.some(
    (addon) => addon.id && addon.name === AddonName.CustomerManagement,
  );

  const is_stock_management_active = authUser?.active_addons?.some(
    (addon) => addon.id && addon.name === AddonName.Stock,
  );

  const is_ticketing_addon_active = authUser?.active_addons?.some(
    (addon) => addon.id && addon.name === AddonName.Ticketing,
  );

  useEffect(() => {
    if (state?.items) {
      SetCartData(state);
    } else {
      onBack?.();
    }
    // eslint-disable-next-line
  }, [state]);

  useEffect(() => {
    setTransaction(false);
    setReturnAmountMsg("");
    setSelectedPaymentType(TransactionType.Cash);
    setCustomerType("individual");
    setSelectedCustomer("");
    checkoutForm.resetFields();
  }, [checkoutForm]);

  useEffect(() => {
    let tax = 0;
    let total = 0;

    const cartitems = cartData?.items;

    cartitems &&
      cartitems.forEach((product) => {
        const productSubTotal = product.price * product.quantity;
        let productTax = 0;

        if ((product.is_taxable || product.tax) && product.taxes?.length) {
          product.taxes.forEach((taxItem) => {
            productTax += productSubTotal * (Number(taxItem.rate) / 100);
          });
        }

        tax += productTax;
        total += productSubTotal + productTax;
      });

    setTotalTax(roundOff(tax));
    setTotalAmount(roundOff(total));
  }, [cartData, roundOff]);

  useEffect(() => {
    getBranchDiscounts()
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setBranchDiscounts(response?.data?.data?.discounts || []);
      })
      .catch((error) => handleApiError(error, t));
  }, [t]);

  useEffect(() => {
    is_customer_management_active &&
      authUser?.can_view_customer &&
      getCustomers({ per_page: -1 })
        .then((response) => {
          if (response?.data?.success === false) {
            handleApiError(response, t);
            return;
          }

          setCustomerData(response?.data?.data?.customers || []);
        })
        .catch((error) => handleApiError(error, t));
  }, [reloadCustomerData, is_customer_management_active, authUser, t]);

  useEffect(() => {
    getRegions()
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setRegions(response?.data?.data?.regions || []);
      })
      .catch((error) => handleApiError(error, t));
  }, [t]);

  const isEnabledFlag = (value) =>
    value === true || value === 1 || value === "1";

  const isTicketItem = (item) => isEnabledFlag(item?.is_ticket);

  const isTicketIssued = (item) => isEnabledFlag(item?.ticket_is_issued);

  const getIssueableTicketItems = (items = []) => {
    if (!Array.isArray(items)) return [];

    return items.filter((item) => isTicketItem(item) && !isTicketIssued(item));
  };

  const getTicketIssueSourceItems = () => {
    if (Array.isArray(transaction?.items) && transaction.items.length > 0) {
      return transaction.items;
    }

    return Array.isArray(cartData?.items) ? cartData.items : [];
  };

  const hasTicketItems =
    getIssueableTicketItems(getTicketIssueSourceItems()).length > 0;

  const checkoutFormInitialValues = {
    payment_type: TransactionType.Cash,
    customer_type: "individual",
    collected_by_cash_multi: "",
    collected_by_card_multi: "",
    dynamic_field: "",
  };

  const getCustomerArabicName = (customer) => {
    if (!customer) return "";
    return customer.name_ar || "";
  };

  const getCustomerDropdownName = (customer) => {
    if (!customer) return "";
    return appConfig.direction === "rtl"
      ? customer.name_ar || customer.name_en
      : customer.name_en || customer.name_ar;
  };

  const filteredCustomerData = customerData.filter(
    (customer) => customer.user_type === customerType,
  );

  const isCompanyCustomer = customerType === "company";

  const resetSelectedCustomer = () => {
    checkoutForm.setFieldsValue({
      customer_id: "",
      buyer_phone: "",
      buyer_company_name: "",
      buyer_company_vat: "",
      buyer_company_address: "",
      buyer_company_city: "",
      buyer_company_postal_code: "",
      buyer_company_district: "",
      buyer_company_building_number: "",
    });

    setCities([]);
    setSelectedCustomer("");
  };

  const handleCityRequestOnLoad = useCallback(
    (value) => {
      if (!value) return;
      setCitiesLoading(true);
      getCities(value)
        .then((response) => {
          if (response?.data?.success === false) {
            handleApiError(response, t);
            return;
          }

          setCities(response?.data?.data?.cities || []);
        })
        .catch((error) => handleApiError(error, t))
        .finally(() => {
          setCitiesLoading(false);
        });
    },
    [t],
  );

  const getRegionNameAr = (id) => {
    const region = regions.find((r) => r.id === id);
    return region?.name_ar || "";
  };

  const getCityNameAr = (id) => {
    const city = cities.find((c) => c.id === id);
    return city?.name_ar || "";
  };

  const handleCustomerSelect = (id) => {
    const customer = customerData.find(
      (customerItem) => customerItem.id === id,
    );

    if (!customer) {
      resetSelectedCustomer();
      return;
    }

    checkoutForm.setFieldsValue({
      buyer_phone: customer?.phone || "",
      buyer_company_name: getCustomerArabicName(customer),
      buyer_company_vat: customer?.vat || "",
      buyer_company_address: customer?.street || "",
      buyer_company_city: customer?.city?.id || "",
      buyer_company_postal_code: customer?.postal_code || "",
      buyer_company_district: customer?.region?.id || "",
      buyer_company_building_number: customer?.building_number || "",
    });

    if (customer?.region?.id) {
      handleCityRequestOnLoad(customer.region.id);
    } else {
      setCities([]);
    }

    setSelectedCustomer(customer);
  };

  const handleCheckout = () => {
    if (isSubmitting || transaction) return;

    const billTotalAmount = roundOff(
      discountValue === 0 ? totalAmount : grandTotalAmount,
    );

    if (Number(billTotalAmount) <= 0) {
      showAppError(t("checkout_total_must_be_greater_than_zero"));
      return;
    }

    setIsSubmitting(true);

    checkoutForm
      .validateFields()
      .then((values) => {
        const transactionObj = {
          total: billTotalAmount,
        };

        if (cartData?.order_type) {
          transactionObj.order_type = cartData.order_type;
        }

        if (selectedPaymentType === TransactionType.Cash) {
          const amountDiff =
            parseFloat(values.dynamic_field || 0) - billTotalAmount;

          if (amountDiff < 0) {
            showAppError(t("ask_more_cash"));
            throw new Error("INSUFFICIENT_CASH");
          }

          if (amountDiff > 0) {
            const msg = (
              <>
                {t("return_back_amount") + " "}
                <CurrencyFormatter value={roundOff(amountDiff)} />
              </>
            );
            setReturnAmountMsg(msg);
          }

          transactionObj.cash_collected = Number(values.dynamic_field);
        } else {
          if (selectedPaymentType === TransactionType.Multipayment) {
            const cashCollect =
              Number(values.collected_by_cash_multi) +
              Number(values.collected_by_card_multi);

            const amountDiff = parseFloat(cashCollect || 0) - billTotalAmount;

            if (amountDiff < 0) {
              showAppError(t("ask_more_cash"));
              throw new Error("INSUFFICIENT_CASH");
            }

            transactionObj.multipayments = JSON.stringify([
              {
                transaction_type: TransactionType.Cash,
                amount: Number(values.collected_by_cash_multi),
              },
              {
                transaction_type: TransactionType.Credit,
                amount: Number(values.collected_by_card_multi),
              },
            ]);
          }

          transactionObj.reference = values.dynamic_field;
        }

        transactionObj.type = values.payment_type;

        transactionObj.items = JSON.stringify(
          cartData.items.map((item) => {
            let tagIds = [];

            if (item.selectedTagIds && item.selectedTagIds.length > 0) {
              tagIds = item.selectedTagIds;
            } else if (item.tags && item.tags.length > 0) {
              tagIds = item.tags.map((tag) => tag.id);
            }

            return {
              id: item.product_id ? item.product_id : item.id,
              qty: item.quantity,
              price: item.price,
              tags: tagIds,
            };
          }),
        );

        if (values.customer_id && is_customer_management_active) {
          transactionObj.customer_id = values.customer_id;
        }

        if (values.buyer_company_name) {
          transactionObj.buyer_company_name = values.buyer_company_name;
        }

        if (values.buyer_phone) {
          transactionObj.buyer_phone = values.buyer_phone;
        }

        if (values.buyer_company_vat) {
          transactionObj.buyer_company_vat = values.buyer_company_vat;
        }

        if (values.buyer_company_address) {
          transactionObj.buyer_company_address = values.buyer_company_address;
        }

        if (values.buyer_company_postal_code) {
          transactionObj.buyer_company_postal_code =
            values.buyer_company_postal_code;
        }

        if (values.buyer_company_city) {
          transactionObj.buyer_company_city = getCityNameAr(
            values.buyer_company_city,
          );
        }

        if (values.buyer_company_district) {
          transactionObj.buyer_company_district = getRegionNameAr(
            values.buyer_company_district,
          );
        }

        if (values.buyer_company_building_number) {
          transactionObj.buyer_company_building_number =
            values.buyer_company_building_number;
        }

        if (discountId) {
          transactionObj.discount_id = discountId;
        }

        if (cartData.diningTableId) {
          transactionObj.dining_table_id = cartData.diningTableId;
        }

        if (cartData.waiterId) {
          const checkoutBranchId =
            cartData.branchId || getEntityBranchId(authUser);
          const waiterBranchId =
            cartData.waiterBranchId || cartData.waiter_branch_id;

          if (
            waiterBranchId &&
            !isSameBranch(waiterBranchId, checkoutBranchId)
          ) {
            showAppError(t("waiter_not_available_for_branch"));
            return;
          }

          transactionObj.waiter_id = cartData.waiterId;
        }

        if (cartData.orderStatus === TransactionStatus.Completed) {
          transactionObj.transaction_status = cartData.orderStatus;
        }

        if (cartData.id) {
          transactionObj.id = cartData.id;
        }

        if (transactionObj.transaction_status) {
          return editTransaction(transactionObj);
        }

        return createTransactions(transactionObj);
      })
      .then((response) => {
        if (!response) return;

        const data = response.data || response.response?.data;

        if (data?.success === true) {
          const tx = response.data.data.transaction;
          setTransaction(tx);

          onDone?.();

          setSelectedPaymentType(TransactionType.Cash);
          setCustomerType("individual");
          setSelectedCustomer("");
          checkoutForm.resetFields();
        } else if (data?.success === false) {
          handleApiError(response, t);
        }
      })
      .catch((err) => {
        if (err?.errorFields) return;
        if (err?.message === "INSUFFICIENT_CASH") return;

        const status = err?.response?.status;
        const data = err?.response?.data?.data || {};
        const zatcaErrors = Array.isArray(data?.zatca_errors)
          ? data.zatca_errors
          : [];

        if (status === 422 && zatcaErrors.length) {
          const top = zatcaErrors.slice(0, 3);
          showAppError(t("zatca_sale_clearance_failed_helper"));
          top.forEach((error) => {
            showAppError(
              [error?.code, error?.message].filter(Boolean).join(" — "),
              t,
            );
          });
          return;
        }

        if (status === 503) {
          Modal.confirm({
            title: t("zatca_sale_clearance_timeout_title"),
            content: t("zatca_sale_clearance_timeout_helper"),
            okText: t("retry_clearance"),
            cancelText: t("issue_b2c_instead"),
            onOk: () => handleCheckout(),
            onCancel: () => {
              checkoutForm.setFieldsValue({
                buyer_phone: "",
                buyer_company_name: "",
                buyer_company_vat: "",
                buyer_company_address: "",
                buyer_company_city: "",
                buyer_company_postal_code: "",
                buyer_company_district: "",
                buyer_company_building_number: "",
              });
              setCustomerType("individual");
              handleCheckout();
            },
          });
          return;
        }

        handleApiError(err, t);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handlePrintTicket = () => {
    if (isTicketIssuing) return;

    const issueableTicketItems = getIssueableTicketItems(
      getTicketIssueSourceItems(),
    );

    if (!issueableTicketItems.length) {
      showAppError(t("no_unissued_ticket_found"));
      return;
    }

    const transactionObj = issueableTicketItems.map((item) => ({
      product_id: item.product_id ?? item.id,
      quantity: item.quantity,
      transaction_id: transaction.id,
      ...(is_stock_management_active &&
        item.expiry_date && { expiry_date: item.expiry_date }),
    }));

    setIsTicketIssuing(true);

    createTickets(transactionObj)
      .then((response) => {
        const data = response.data || response.response?.data;
        if (data?.success === true) {
          setTransaction((prevTransaction) => ({
            ...prevTransaction,
            items: prevTransaction?.items?.map((item) =>
              isTicketItem(item) ? { ...item, ticket_is_issued: 1 } : item,
            ),
          }));

          navigate("/print-ticket", {
            state: { tickets: data.data.tickets },
          });
        } else {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsTicketIssuing(false);
      });
  };

  const handleCashMultiPayment = (e) => {
    const amount = e.target.value;
    checkoutForm.setFieldsValue({
      collected_by_cash_multi: amount,
      collected_by_card_multi: (
        (discountValue === 0 ? totalAmount : grandTotalAmount) - amount
      ).toFixed(2),
    });
  };

  const handleCardMultiPayment = (e) => {
    const amount = e.target.value ? e.target.value : e;
    checkoutForm.setFieldsValue({
      collected_by_card_multi: amount,
      collected_by_cash_multi: (
        (discountValue === 0 ? totalAmount : grandTotalAmount) - amount
      ).toFixed(2),
    });
  };

  const handleDiscount = (id) => {
    if (authUser.allow_discount === true) {
      const discount_id = id;
      const discount = branchDiscounts.find(
        (discountItem) => discountItem.id === discount_id,
      );
      setDiscountId(discount_id);
      setDiscountValue(discount?.discount_percentage || 0);

      if (discount) {
        const discount_percentage = discount.discount_percentage / 100;
        const amountValue = totalAmount * discount_percentage;
        const taxValue = totalTax * discount_percentage;
        setGrandTotalTax(totalTax - taxValue);
        setGrandTotalAmount(totalAmount - amountValue);
      }
    }
  };

  const discountReset = () => {
    setGrandTotalTax(0);
    setGrandTotalAmount(0);
    setDiscountValue(0);
    setDiscountId("");
  };

  useEffect(() => {
    if (onScreenInput > 0) {
      if (selectedField === "collected_by_cash_multi") {
        checkoutForm.setFieldsValue({
          collected_by_cash_multi: onScreenInput,
          collected_by_card_multi: (
            (discountValue === 0 ? totalAmount : grandTotalAmount) -
            onScreenInput
          ).toFixed(2),
        });
      }
      if (selectedField === "collected_by_card_multi") {
        checkoutForm.setFieldsValue({
          collected_by_card_multi: onScreenInput,
          collected_by_cash_multi: (
            (discountValue === 0 ? totalAmount : grandTotalAmount) -
            onScreenInput
          ).toFixed(2),
        });
      }
      if (selectedField === "dynamic_field") {
        setCashCollected(onScreenInput);
        checkoutForm.setFieldsValue({
          dynamic_field: onScreenInput,
        });
      }
    }
  }, [
    onScreenInput,
    checkoutForm,
    selectedField,
    discountValue,
    totalAmount,
    grandTotalAmount,
  ]);

  const handleButtonClick = (value) => {
    if (value !== "Clear") {
      setOnScreenInput((prevValue) => prevValue + value);
    } else {
      setOnScreenInput("");
      if (selectedField === "collected_by_cash_multi") {
        checkoutForm.setFieldsValue({
          collected_by_cash_multi: 0,
          collected_by_card_multi: (
            (discountValue === 0 ? totalAmount : grandTotalAmount) - 0
          ).toFixed(2),
        });
      }
      if (selectedField === "collected_by_card_multi") {
        checkoutForm.setFieldsValue({
          collected_by_card_multi: 0,
          collected_by_cash_multi: (
            (discountValue === 0 ? totalAmount : grandTotalAmount) - 0
          ).toFixed(2),
        });
      }
      if (selectedField === "dynamic_field") {
        setCashCollected("0");
        checkoutForm.setFieldsValue({
          dynamic_field: 0,
        });
      }
    }
  };

  const handleSelectField = (data) => {
    setSelectedField(data);
    setOnScreenInput("");
  };

  const sectionTitleStyle = {
    fontSize: "13px",
    fontWeight: 600,
    color: "var(--color-text)",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    margin: "8px 0 12px",
    paddingBottom: "8px",
    borderBottom: "1px solid var(--color-bg)",
  };

  const compactFormItemStyle = { marginBottom: "12px" };

  const filterByLabel = (input, option) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  const getPaymentTypeLabel = (type = selectedPaymentType) => {
    if (type === TransactionType.Cash) return t("cash");
    if (type === TransactionType.Credit) return t("card");
    if (type === TransactionType.Multipayment) return t("multi_payment");

    return "-";
  };

  const getTransactionTotal = () =>
    transaction?.amount_charged ??
    transaction?.total ??
    transaction?.sub_total ??
    transaction?.subtotal ??
    0;

  const checkoutTotalAmount = roundOff(
    discountValue === 0 ? totalAmount : grandTotalAmount,
  );

  const isCheckoutTotalInvalid = Number(checkoutTotalAmount) <= 0;

  const actionBarStyle = {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    paddingTop: "16px",
  };

  const renderActionButtons = (style = {}) => (
    <Space wrap style={{ ...actionBarStyle, ...style }}>
      {transaction === false && (
        <Button
          key="proceed"
          type="primary"
          onClick={handleCheckout}
          loading={isSubmitting}
          disabled={isSubmitting || isCheckoutTotalInvalid}
        >
          {t("proceed")}
        </Button>
      )}

      {transaction && (
        <>
          <ReactToPrint
            trigger={() => (
              <Button key="print" type="primary">
                {t("print")}
              </Button>
            )}
            content={() => transactionSlipRef.current}
            documentTitle={transaction.uid}
          />
          {hasTicketItems && is_ticketing_addon_active ? (
            <Button
              type="primary"
              onClick={handlePrintTicket}
              loading={isTicketIssuing}
              disabled={isTicketIssuing}
            >
              {t("issue_ticket")}
            </Button>
          ) : null}
        </>
      )}

      <Button
        type="default"
        key="back"
        onClick={() => (onBack ? onBack() : window.history.back())}
      >
        {t("back")}
      </Button>
    </Space>
  );

  const renderSummaryRow = (label, value) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "12px",
        padding: "12px 0",
        borderBottom: "1px solid var(--color-border)",
      }}
    >
      <span style={{ color: "var(--color-text)", fontSize: "13px" }}>
        {label}
      </span>
      <strong
        style={{
          color: "var(--color-heading)",
          fontSize: "14px",
          textAlign: "end",
        }}
      >
        {value}
      </strong>
    </div>
  );

  return (
    <Spin spinning={isSubmitting}>
      <Card
        className={
          transaction === false ? "main_card" : "main_card_transaction"
        }
        style={{
          direction: appConfig.direction
            ? appConfig.direction === "ltr"
              ? "ltr"
              : "rtl"
            : "ltr",
        }}
      >
        <Row gutter={[20, 20]}>
          <Col xs={24} lg={transaction === false ? 16 : 24}>
            {transaction === false ? (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                    gap: "16px",
                    marginBottom: "16px",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "var(--color-text)",
                        marginBottom: "2px",
                      }}
                    >
                      {t("total_taxes")}:{" "}
                      <span
                        style={{
                          color: "var(--color-primary)",
                          fontWeight: 600,
                        }}
                      >
                        <CurrencyFormatter
                          value={discountValue === 0 ? totalTax : grandtotalTax}
                        />
                      </span>
                    </div>
                    <div style={{ fontSize: "20px", fontWeight: 700 }}>
                      {t("total_amount")} :{" "}
                      <span style={{ color: "var(--color-primary)" }}>
                        <CurrencyFormatter
                          value={
                            discountValue === 0 ? totalAmount : grandTotalAmount
                          }
                        />
                      </span>
                    </div>
                  </div>

                  <div style={{ textAlign: "end" }}>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "var(--color-text)",
                        marginBottom: "4px",
                      }}
                    >
                      {t("payment_type")}
                    </div>
                    <div style={{ fontWeight: 600, fontSize: "15px" }}>
                      {selectedPaymentType === TransactionType.Cash &&
                        t("cash")}
                      {selectedPaymentType === TransactionType.Credit &&
                        t("card")}
                      {selectedPaymentType === TransactionType.Multipayment &&
                        t("multi_payment")}
                    </div>
                  </div>
                </div>

                {selectedPaymentType === TransactionType.Cash &&
                  cashCollected && (
                    <Alert
                      style={{ marginBottom: "12px" }}
                      type={
                        cashCollected <
                        (discountValue === 0 ? totalAmount : grandTotalAmount)
                          ? "warning"
                          : "success"
                      }
                      showIcon
                      message={
                        <span>
                          {t("return_back_amount")} :{" "}
                          {cashCollected <
                          (discountValue === 0
                            ? totalAmount
                            : grandTotalAmount) ? (
                            t("collect_more_cash")
                          ) : (
                            <CurrencyFormatter
                              value={
                                cashCollected -
                                (discountValue === 0
                                  ? totalAmount
                                  : grandTotalAmount)
                              }
                            />
                          )}
                        </span>
                      }
                    />
                  )}

                <Form
                  name="checkout-form"
                  layout="vertical"
                  onFinish={handleCheckout}
                  className="app-form-full"
                  form={checkoutForm}
                  initialValues={checkoutFormInitialValues}
                >
                  <Form.Item
                    name="payment_type"
                    style={{ marginBottom: "16px" }}
                  >
                    <Radio.Group
                      onChange={(e) => setSelectedPaymentType(e.target.value)}
                      size="small"
                    >
                      <Space size={[20, 20]} wrap>
                        <Radio.Button
                          className="app-h-auto"
                          value={TransactionType.Cash}
                        >
                          <img
                            src="/images/payment_type_cash.png"
                            alt={t("cash")}
                          />
                        </Radio.Button>
                        <Radio.Button
                          className="app-h-auto"
                          value={TransactionType.Credit}
                        >
                          <img
                            src="/images/payment_type_cc.png"
                            alt={t("card")}
                          />
                        </Radio.Button>
                        <Radio.Button
                          className="app-h-auto"
                          value={TransactionType.Multipayment}
                        >
                          <span
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <img
                              src="/images/payment_type_cash.png"
                              alt={t("cash")}
                            />
                            +
                            <img
                              src="/images/payment_type_cc.png"
                              alt={t("card")}
                            />
                          </span>
                        </Radio.Button>
                      </Space>
                    </Radio.Group>
                  </Form.Item>

                  <Row gutter={[20, 0]}>
                    <Col span={12}>
                      {branchDiscounts.length > 0 &&
                        authUser.allow_discount === true && (
                          <div
                            style={{
                              display: "flex",
                              columnGap: "10px",
                              marginBottom: "12px",
                            }}
                          >
                            <Dropdown
                              trigger={["click"]}
                              menu={{
                                items: branchDiscounts.map((branch) => ({
                                  key: branch.id,
                                  label: (
                                    <div
                                      onClick={() => {
                                        handleDiscount(branch.id);
                                      }}
                                    >
                                      {branch.discount_percentage} %
                                    </div>
                                  ),
                                })),
                              }}
                            >
                              <Button type="default" className="app-w-full">
                                {discountValue === 0
                                  ? t("select_a_discount")
                                  : discountValue + " %"}
                                <DownOutlined />
                              </Button>
                            </Dropdown>
                            {discountValue !== 0 && (
                              <Button
                                type="text"
                                onClick={discountReset}
                                danger
                              >
                                <CloseOutlined />
                              </Button>
                            )}
                          </div>
                        )}

                      {selectedPaymentType === TransactionType.Multipayment && (
                        <Row gutter={[12, 0]}>
                          <Col span={12}>
                            <Form.Item
                              name="collected_by_cash_multi"
                              label={t("collected_by_cash")}
                              rules={[
                                {
                                  required: true,
                                  message: t("validate_cash_required"),
                                },
                              ]}
                              style={compactFormItemStyle}
                            >
                              <Input
                                type="number"
                                onChange={handleCashMultiPayment}
                                onFocus={() =>
                                  handleSelectField("collected_by_cash_multi")
                                }
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              name="collected_by_card_multi"
                              label={t("collected_by_card")}
                              rules={[
                                {
                                  required: true,
                                  message: t("validate_card_required"),
                                },
                              ]}
                              style={compactFormItemStyle}
                            >
                              <Input
                                type="number"
                                onChange={handleCardMultiPayment}
                                onFocus={() =>
                                  handleSelectField("collected_by_card_multi")
                                }
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      )}

                      <Form.Item
                        name="dynamic_field"
                        rules={[
                          {
                            required: true,
                            message:
                              selectedPaymentType === TransactionType.Cash
                                ? t("validate_cash_required")
                                : t("validate_reference_required"),
                          },
                        ]}
                        label={
                          selectedPaymentType === TransactionType.Cash
                            ? t("cash_collected")
                            : t("reference_no")
                        }
                        style={compactFormItemStyle}
                      >
                        <Input
                          size="large"
                          onChange={(e) => {
                            setCashCollected(e.target.value);
                          }}
                          onFocus={() => handleSelectField("dynamic_field")}
                        />
                      </Form.Item>

                      {is_customer_management_active &&
                        authUser.can_view_customer && (
                          <>
                            <Form.Item
                              name="customer_type"
                              label={t("customer_type")}
                              style={compactFormItemStyle}
                            >
                              <Radio.Group
                                value={customerType}
                                onChange={(e) => {
                                  setCustomerType(e.target.value);
                                  resetSelectedCustomer();
                                }}
                              >
                                <Radio value="individual">
                                  {t("individual")}
                                </Radio>
                                <Radio value="company">{t("company")}</Radio>
                              </Radio.Group>
                            </Form.Item>

                            <div
                              style={{
                                display: "flex",
                                alignItems: "flex-end",
                                columnGap: "8px",
                                marginBottom: "8px",
                              }}
                            >
                              <Form.Item
                                name="customer_id"
                                label={t("customer_name")}
                                hasFeedback
                                style={{
                                  width: "100%",
                                  ...compactFormItemStyle,
                                }}
                              >
                                <Select
                                  showSearch
                                  className="app-w-full"
                                  onChange={handleCustomerSelect}
                                  options={filteredCustomerData.map(
                                    (customer) => ({
                                      label: `${getCustomerDropdownName(
                                        customer,
                                      )} ${customer.phone || ""}`,
                                      value: customer.id,
                                    }),
                                  )}
                                  filterOption={filterByLabel}
                                />
                              </Form.Item>

                              {selectedCustomer && (
                                <Button
                                  type="text"
                                  onClick={resetSelectedCustomer}
                                  danger
                                  style={{ marginBottom: "12px" }}
                                >
                                  <CloseOutlined />
                                </Button>
                              )}

                              {authUser.can_add_edit_customer && (
                                <div style={{ marginBottom: "12px" }}>
                                  <AddCustomer
                                    reloadCallback={() =>
                                      setReloadCustomerData(!reloadCustomerData)
                                    }
                                  />
                                </div>
                              )}
                            </div>
                          </>
                        )}
                    </Col>

                    <Col span={12}>
                      <Row gutter={[12, 12]} style={{ direction: "ltr" }}>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, ".", 0, "Clear"].map(
                          (num) => (
                            <Col key={num} span={8}>
                              <Button
                                type="default"
                                block
                                style={{ height: "48px", fontWeight: 600 }}
                                onClick={() => handleButtonClick(num)}
                              >
                                {num === "Clear"
                                  ? t("clear")
                                  : num}
                              </Button>
                            </Col>
                          ),
                        )}
                      </Row>
                    </Col>
                  </Row>

                  {is_customer_management_active &&
                    authUser.can_view_customer && (
                      <>
                        <div style={sectionTitleStyle}>
                          {isCompanyCustomer
                            ? t("buyer_company_information")
                            : t("buyer_information")}
                        </div>

                        <Row gutter={[16, 0]}>
                          <Col span={8}>
                            <Form.Item
                              name="buyer_company_name"
                              label={
                                isCompanyCustomer
                                  ? t("buyer_company_name")
                                  : t("buyer_name")
                              }
                              style={compactFormItemStyle}
                              hasFeedback
                            >
                              <Input disabled dir="rtl" />
                            </Form.Item>
                          </Col>

                          {!isCompanyCustomer && (
                            <Col span={8}>
                              <Form.Item
                                name="buyer_phone"
                                label={t("phone_number")}
                                style={compactFormItemStyle}
                              >
                                <Input disabled />
                              </Form.Item>
                            </Col>
                          )}

                          {isCompanyCustomer && (
                            <>
                              <Col span={8}>
                                <Form.Item
                                  name="buyer_company_vat"
                                  label={t("buyer_company_vat")}
                                  style={compactFormItemStyle}
                                  hasFeedback
                                >
                                  <Input disabled maxLength={15} />
                                </Form.Item>
                              </Col>

                              <Col span={8}>
                                <Form.Item
                                  name="buyer_company_address"
                                  label={t("buyer_company_address")}
                                  style={compactFormItemStyle}
                                  hasFeedback
                                >
                                  <Input disabled />
                                </Form.Item>
                              </Col>

                              <Col span={6}>
                                <Form.Item
                                  name="buyer_company_postal_code"
                                  label={t("postal_code")}
                                  style={compactFormItemStyle}
                                  hasFeedback
                                >
                                  <Input
                                    disabled
                                    maxLength={5}
                                    inputMode="numeric"
                                  />
                                </Form.Item>
                              </Col>

                              <Col span={6}>
                                <Form.Item
                                  name="buyer_company_district"
                                  label={t("region")}
                                  style={compactFormItemStyle}
                                  hasFeedback
                                >
                                  <Select
                                    showSearch
                                    disabled
                                    options={regions.map((region) => ({
                                      label:
                                        appConfig.direction === "ltr"
                                          ? region.name_en
                                          : region.name_ar,
                                      value: region.id,
                                    }))}
                                    filterOption={filterByLabel}
                                  />
                                </Form.Item>
                              </Col>

                              <Col span={6}>
                                <Form.Item
                                  name="buyer_company_city"
                                  label={t("city")}
                                  style={compactFormItemStyle}
                                  hasFeedback
                                >
                                  <Select
                                    showSearch
                                    loading={citiesLoading}
                                    disabled
                                    options={cities.map((city) => ({
                                      label:
                                        appConfig.direction === "ltr"
                                          ? city.name_en
                                          : city.name_ar,
                                      value: city.id,
                                    }))}
                                    filterOption={filterByLabel}
                                  />
                                </Form.Item>
                              </Col>

                              <Col span={6}>
                                <Form.Item
                                  name="buyer_company_building_number"
                                  label={t("building_number")}
                                  style={compactFormItemStyle}
                                  hasFeedback
                                >
                                  <Input
                                    disabled
                                    maxLength={4}
                                    inputMode="numeric"
                                  />
                                </Form.Item>
                              </Col>
                            </>
                          )}
                        </Row>
                      </>
                    )}
                </Form>
              </>
            ) : (
              <Row
                gutter={[18, 18]}
                style={{ minHeight: "calc(100vh - 150px)" }}
              >
                <Col xs={24} lg={7}>
                  <div
                    style={{
                      height: "100%",
                      padding: "18px",
                      border: "1px solid var(--color-border)",
                      borderRadius: "18px",
                      background: "var(--color-white)",
                      boxShadow: "0 10px 30px var(--color-shadow-soft)",
                    }}
                  >
                    {returnAmountMsg && (
                      <Alert
                        message={returnAmountMsg}
                        type="info"
                        showIcon
                        style={{ marginBottom: "14px" }}
                      />
                    )}

                    <div
                      style={{
                        marginBottom: "14px",
                        padding: "16px",
                        borderRadius: "16px",
                        background: "var(--color-primary-soft)",
                        border: "1px solid var(--color-border)",
                      }}
                    >
                      <span
                        style={{
                          display: "block",
                          marginBottom: "6px",
                          color: "var(--color-text)",
                          fontSize: "12px",
                          fontWeight: 600,
                        }}
                      >
                        {t("total_amount")}
                      </span>
                      <strong
                        style={{
                          display: "block",
                          color: "var(--color-primary)",
                          fontSize: "24px",
                          fontWeight: 800,
                        }}
                      >
                        <CurrencyFormatter value={getTransactionTotal()} />
                      </strong>
                    </div>

                    {renderSummaryRow(t("transaction_id"), transaction?.uid)}
                    {renderSummaryRow(
                      t("payment_type"),
                      getPaymentTypeLabel(transaction?.type),
                    )}
                    {transaction?.reference
                      ? renderSummaryRow(
                          t("reference_no"),
                          transaction.reference,
                        )
                      : null}

                    {renderActionButtons({
                      paddingTop: "16px",
                    })}
                  </div>
                </Col>

                <Col xs={24} lg={17}>
                  <div
                    style={{
                      height: "100%",
                      padding: "16px",
                      border: "1px solid var(--color-border)",
                      borderRadius: "18px",
                      background: "var(--color-white)",
                      boxShadow: "0 10px 30px var(--color-shadow-soft)",
                      display: "flex",
                      flexDirection: "column",
                      minHeight: 0,
                    }}
                  >
                    <div
                      style={{
                        marginBottom: "12px",
                      }}
                    >
                      <strong style={{ color: "var(--color-heading)" }}>
                        {t("preview")}
                      </strong>
                    </div>

                    <div
                      style={{
                        flex: 1,
                        minHeight: 0,
                        maxHeight: "calc(100vh - 210px)",
                        overflow: "auto",
                        padding: "18px",
                        borderRadius: "16px",
                        border: "1px dashed var(--color-border)",
                        background: "var(--color-bg)",
                      }}
                    >
                      <div
                        style={{
                          width: "500px",
                          maxWidth: "100%",
                          margin: "0 auto",
                          borderRadius: "14px",
                          background: "var(--color-white)",
                          boxShadow: "0 14px 36px var(--color-shadow)",
                          overflow: "hidden",
                        }}
                      >
                        <TransactionSlip
                          ref={transactionSlipRef}
                          transaction={transaction}
                          shop={{
                            name: authUser.business_name,
                            vat: authUser.vat,
                            logo: authUser.logo,
                            is_vat_exempt: authUser.is_vat_exempt,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            )}

            {transaction === false && renderActionButtons()}
          </Col>

          {transaction === false && (
            <Col xs={24} lg={8}>
              <strong>{t("selected_item")} :</strong>
              {cartData?.items &&
                cartData?.items.map((item) => (
                  <div
                    style={{
                      padding: "10px",
                      borderBottom: "1px solid var(--color-border)",
                    }}
                    key={item.id}
                  >
                    <div className="app-flex-gap-10">
                      <img
                        alt={item.name_en}
                        src={
                          item.image
                            ? item.image
                            : "/images/image-not-found.png"
                        }
                        style={{
                          width: "50px",
                          height: "auto",
                          cursor: "pointer",
                          borderRadius: "1rem",
                          border: "1px solid var(--color-border)",
                        }}
                      />
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          width: "calc(100% - 100px)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            marginBottom: "10px",
                          }}
                        >
                          <span dir="ltr">
                            {item.quantity} x{" "}
                            <bdi dir="auto">
                              {appConfig.direction === "rtl"
                                ? item.name
                                : item.name_en}
                            </bdi>
                          </span>
                        </div>
                        <div style={{ fontSize: "12px", lineHeight: "1" }}>
                          <div>
                            <CurrencyFormatter value={item.price} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </Col>
          )}
        </Row>
      </Card>
    </Spin>
  );
};

export default Checkout;
